'use client';

import React from 'react';
import SlideWrapper from '../SlideWrapper';
import { Gift, Award, Briefcase, GraduationCap } from 'lucide-react';

interface Props {
  id: string;
  registerRef: (id: string, el: HTMLElement | null) => void;
}

export default function Slide14Benefits({ id, registerRef }: Props) {
  return (
    <SlideWrapper id={id} registerRef={registerRef} className="bg-[#0f2318]">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1714924268038-92caf18ebbca"
          alt="Thiên nhiên xanh tươi rừng nhiệt đới Việt Nam với ánh sáng ban mai"
          className="w-full h-full object-cover opacity-10" />

        <div className="absolute inset-0 bg-gradient-to-tr from-[#1a3a2a]/70 via-[#0f2318]/90 to-[#0f2318]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(76,175,125,0.4)] to-transparent" />
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-16 xl:px-24 py-24 pt-28 min-h-screen flex flex-col justify-center">
        <div className="fade-in-up mb-4">
          <div className="chapter-badge">
            <Gift size={12} />
            Quyền lợi sinh viên
          </div>
        </div>

        <div className="fade-in-up mb-3">
          <h2 className="font-display font-800 text-[clamp(28px,4.5vw,56px)] leading-[1.1] text-[#f0ede6] tracking-tight">
            Quyền lợi dành cho sinh viên
          </h2>
          <p className="font-body text-[15px] lg:text-[16px] text-[#a0b8a8] mt-3">
            Sinh viên tham gia chương trình sẽ nhận được những quyền lợi thiết thực sau:
          </p>
        </div>

        <div className="fade-in-up mb-10">
          <div className="section-divider" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 fade-in-up mb-8">
          {[
          {
            icon: <Gift size={32} className="text-[#4caf7d]" />,
            title: 'Trải nghiệm hoàn toàn miễn phí 8 ngày 7 đêm tại đảo CEO',
            detail: 'Sinh viên được tham gia chương trình hoàn toàn miễn phí với giá trị tương đương 10.000.000 VNĐ/người/gói. Đây không chỉ là một chuyến đi tình nguyện, mà còn là cơ hội để các bạn được nghỉ ngơi, trải nghiệm thiên nhiên và tái tạo năng lượng trong một không gian có giá trị thực tế cao.',
            note: '~10.000.000 VNĐ/người',
            color: '#4caf7d'
          },
          {
            icon: <Award size={32} className="text-[#e8a030]" />,
            title: 'Chứng nhận Green Warrior',
            detail: 'Sau khi hoàn thành chương trình, mỗi sinh viên sẽ được nhận chứng nhận Green Warrior như một dấu mốc ghi nhận cho hành trình tham gia và đóng góp của mình. Chứng nhận này có thể sử dụng cho hồ sơ cá nhân, CV, portfolio, hoạt động Đoàn – Hội hoặc các chương trình xét điểm rèn luyện, ghi nhận ngoại khóa.',
            note: 'Có giá trị trong CV & portfolio',
            color: '#e8a030'
          },
          {
            icon: <Briefcase size={32} className="text-[#a8d87a]" />,
            title: 'Trải nghiệm tình nguyện thực tế, không hình thức',
            detail: 'Sinh viên không tham gia theo kiểu có mặt cho đủ, mà trực tiếp bắt tay vào những phần việc cụ thể, nhìn thấy rõ kết quả từ những gì mình đã làm. Điều này giúp các bạn cảm nhận được giá trị thật của hành trình, thay vì cảm giác tham gia cho có phong trào.',
            note: 'Kỹ năng áp dụng được ngay',
            color: '#a8d87a'
          }].
          map((item, i) =>
          <div
            key={`benefit-${i}`}
            className="rounded-2xl p-8 flex flex-col transition-all duration-300 hover:-translate-y-2 hover:shadow-lg relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${item.color}15 0%, ${item.color}05 100%)`,
              border: `1px solid ${item.color}30`,
              boxShadow: `0 0 40px ${item.color}08`
            }}>

              <div
              className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
              style={{ background: `linear-gradient(90deg, ${item.color}80, ${item.color}20)` }} />

              <div
              className="p-4 rounded-2xl inline-flex mb-5"
              style={{ background: `${item.color}15` }}>

                {item.icon}
              </div>
              <h3
              className="font-display font-700 text-[17px] lg:text-[18px] mb-4 leading-snug"
              style={{ color: item.color }}>

                {item.title}
              </h3>
              <p className="font-body text-[13px] lg:text-[14px] text-[#a0b8a8] leading-relaxed flex-1 mb-4">{item.detail}</p>
              <div
              className="inline-block px-3 py-1 rounded-full text-[11px] font-600 font-body w-fit"
              style={{ background: `${item.color}15`, color: item.color }}>

                {item.note}
              </div>
            </div>
          )}
        </div>

        <div className="fade-in-up glass-card p-5 lg:p-6 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
          <GraduationCap size={32} className="text-[#4caf7d] flex-shrink-0" />
          <p className="font-body text-[15px] text-[#a0b8a8]">
            Chương trình <span className="text-[#f0ede6] font-600">hoàn toàn miễn phí</span> cho sinh viên được chọn. Tri An Green Travel đầu tư toàn bộ để đảm bảo trải nghiệm{' '}
            <span className="text-[#4caf7d] font-600">chất lượng cao nhất</span>.
          </p>
        </div>
      </div>
    </SlideWrapper>);

}